import { useCallback, useEffect, useRef, useState } from 'react'
import { HubSpotForm } from '@/components/marketing/HubSpotForm'
import { buttonVariants } from '@/components/ui/button.variants'
import { cn } from '@/lib/cn'

/**
 * "Give your email, get the PDF" inside MDX: a card with a button that opens
 * a HubSpot form in a dialog, then swaps the form for a download link.
 *
 * Soft gate: the file is a normal public asset; the form captures the people
 * who want it. `autoOpenAt` (0 to 1, share of the page scrolled) also opens the
 * dialog once per visitor, remembered in localStorage.
 *
 * HubSpot reports a submission two ways depending on the form's editor: the
 * new embed fires `hs-form-event:on-submission:success` on window, legacy forms
 * post an `hsFormCallback` message. Either one reveals the download.
 */
export function GatedDownload({
  formId,
  file,
  title,
  description,
  buttonLabel = 'Get the PDF',
  autoOpenAt,
}: {
  formId: string
  file: string
  title: string
  description: string
  buttonLabel?: string
  autoOpenAt?: number
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [open, setOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const seenKey = `sg-gated-download:${formId}`

  const show = useCallback(() => {
    setOpen(true)
    dialog.current?.showModal()
  }, [])

  useEffect(() => {
    const onSuccess = (event: Event) => {
      const id = (event as CustomEvent<{ formId?: string }>).detail?.formId
      if (!id || id === formId) setSubmitted(true)
    }
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; eventName?: string; id?: string } | null
      if (data?.type !== 'hsFormCallback' || data.eventName !== 'onFormSubmitted') return
      if (!data.id || data.id === formId) setSubmitted(true)
    }
    window.addEventListener('hs-form-event:on-submission:success', onSuccess)
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('hs-form-event:on-submission:success', onSuccess)
      window.removeEventListener('message', onMessage)
    }
  }, [formId])

  useEffect(() => {
    if (autoOpenAt === undefined) return
    let seen = false
    try {
      seen = window.localStorage.getItem(seenKey) !== null
    } catch {
      seen = false
    }
    if (seen) return
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0 || window.scrollY / scrollable < autoOpenAt) return
      window.removeEventListener('scroll', onScroll)
      try {
        window.localStorage.setItem(seenKey, '1')
      } catch {
        // Private mode or blocked storage: it may open again next visit, which is fine.
      }
      if (!dialog.current?.open) show()
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [autoOpenAt, seenKey, show])

  return (
    <aside className="my-10 rounded-2xl bg-blue-10 p-8">
      <p className="mt-0 mb-2 font-heading text-xl font-medium">{title}</p>
      <p className="mt-0 mb-6 text-font-secondary">{description}</p>
      <button type="button" onClick={show} className={buttonVariants({ variant: 'primary', size: 'small' })}>
        {buttonLabel}
      </button>

      <dialog
        ref={dialog}
        onClose={() => setOpen(false)}
        aria-label={title}
        className="m-auto max-h-[90vh] w-full max-w-[440px] overflow-y-auto rounded-2xl border-0 bg-white p-6 backdrop:bg-black"
      >
        <div className="mb-2 flex items-start justify-between gap-4">
          <p className="m-0 font-heading text-lg font-medium">{title}</p>
          <button
            type="button"
            onClick={() => dialog.current?.close()}
            aria-label="Close"
            className="cursor-pointer border-0 bg-transparent p-1 text-xl leading-none text-font-secondary"
          >
            ×
          </button>
        </div>
        {submitted ? (
          <div>
            <p className="mt-0 mb-6">Thanks. It's yours.</p>
            <a href={file} download className={cn(buttonVariants({ variant: 'primary' }), 'no-underline')}>
              Download the PDF
            </a>
          </div>
        ) : (
          <>
            <p className="mt-0 mb-2 text-small text-font-secondary">Enter your email and it's yours.</p>
            {open && <HubSpotForm formId={formId} className="min-h-0" />}
          </>
        )}
      </dialog>
    </aside>
  )
}
